import { forwardRef, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '@src/components/Panel/Panel';
import { Button } from '@src/components/Button/Button';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { Input } from '@src/components/Input/Input';
import { Pagination, type PaginationProps } from '@src/components/Pagination/Pagination';
import { SearchIcon } from '@src/components/icons/SearchIcon';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './ParticipantTable.css';

export interface ParticipantTableSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  clearLabel: string;
}

export interface ParticipantTableProps {
  /** Render inside an existing panel without another surface or decorations. */
  embedded?: boolean;
  /** Caps label above the toolbar — e.g. box recover. */
  label?: string;
  showSearch?: boolean;
  /** Collapse search into an icon beside the participant range. */
  compactSearch?: boolean;
  /** Show the participant count between the toolbar and rows. */
  rangeBelowToolbar?: boolean;
  rangeControls?: ReactNode;
  search?: ParticipantTableSearchProps;
  isEmpty?: boolean;
  empty?: ReactNode;
  children: ReactNode;
  /** Actions below the list and pagination, inside the panel. */
  actions?: ReactNode;
  /** Replaces pagination with table-local selection controls. */
  footer?: ReactNode;
  pagination?: Omit<PaginationProps, 'testId'> & {
    testId?: string;
    total: number;
    pageSize: number;
  };
  /** Right side of the range row — e.g. reset exclusions on the randomizer. */
  rangeEnd?: ReactNode;
  /** Additional controls beside compact search. */
  toolbarActions?: ReactNode;
  /** Optional input replacing the compact toolbar content. */
  toolbarContent?: ReactNode | ((searchToggle: ReactNode) => ReactNode);
  toolbarReplaced?: boolean;
  /** Top of the card — e.g. a names textarea before the row list. */
  compose?: ReactNode;
  /** Rule between compose and the list; off when the list is still empty. */
  composeDivider?: boolean;
  testId?: string;
}

/** Snowy panel shell for participant lists — shared search and pagination. */
export const ParticipantTable = forwardRef<HTMLDivElement, ParticipantTableProps>(
  function ParticipantTable(
    {
      label,
      embedded = false,
      showSearch = false,
      compactSearch = false,
      rangeBelowToolbar = false,
      rangeControls,
      search,
      isEmpty = false,
      empty,
      children,
      actions,
      footer,
      pagination,
      rangeEnd,
      toolbarActions,
      toolbarContent,
      toolbarReplaced = false,
      compose,
      composeDivider = true,
      testId,
    },
    ref,
  ) {
    const { t } = useTranslation();
    const [searchOpen, setSearchOpen] = useState(false);
    const searchButton = useRef<HTMLButtonElement>(null);
    const searchId = useId();
    const expanded = searchOpen || Boolean(search?.value);
    function closeSearch() {
      search?.onChange('');
      setSearchOpen(false);
      searchButton.current?.focus();
    }
    const showPagination = Boolean(pagination && pagination.pageCount > 1);
    const rangeFrom =
      pagination && pagination.total > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
    const rangeTo = pagination
      ? Math.min(pagination.page * pagination.pageSize, pagination.total)
      : 0;

    const searchInput = search ? (
      <Input
        id={searchId}
        autoFocus={compactSearch}
        onKeyDown={(event) => {
          if (compactSearch && event.key === 'Escape') {
            event.preventDefault();
            closeSearch();
          }
        }}
        value={search.value}
        onValueChange={search.onChange}
        type="search"
        grow
        iconStart={<SearchIcon />}
        placeholder={search.placeholder}
        aria-label={search.placeholder}
        onClear={
          search.value
            ? () => {
                search.onChange('');
              }
            : undefined
        }
        clearLabel={search.clearLabel}
        testId={testIdFor(testId, 'search')}
      />
    ) : null;
    const range = pagination ? (
      <Typography
        as="p"
        role="meta/helper"
        className="participant-table__range"
        {...testIdAttr(testIdFor(testId, 'pages-range'))}
      >
        {t('box.organizer.pages.range', {
          from: rangeFrom,
          to: rangeTo,
          total: pagination.total,
        })}
      </Typography>
    ) : null;

    const searchToggle = !toolbarReplaced && showSearch && search ? (
                  <Button
                    ref={searchButton}
                    variant="ghost"
                    icon={expanded ? <CloseIcon /> : <SearchIcon />}
                    aria-label={expanded ? t('box.close') : search.placeholder}
                    aria-expanded={expanded}
                    aria-controls={expanded ? searchId : undefined}
                    onClick={() => (expanded ? closeSearch() : setSearchOpen(true))}
                    className="participant-table__search-toggle"
                    testId={testIdFor(testId, 'search-toggle')}
                  />
                ) : null;

    const TableSurface = embedded ? 'div' : Panel;

    return (
      <div ref={ref} className="participant-table-host">
        <TableSurface
          {...(embedded
            ? testIdAttr(testId)
            : { elevated: true, wide: true, edges: { pieces: 3 as const }, testId })}
          className="participant-table participant-table--fit"
        >
          {label ? (
            <Typography as="span" role="meta/caps">
              {label}
            </Typography>
          ) : null}

          {compose ? (
            <div
              className={
                composeDivider
                  ? 'participant-table__compose'
                  : 'participant-table__compose participant-table__compose--plain'
              }
            >
              {compose}
            </div>
          ) : null}

          {compactSearch ? (
            (showSearch && search) || showPagination || rangeEnd || toolbarActions || toolbarContent ? (
              <div className="participant-table__toolbar participant-table__toolbar--compact">
                {typeof toolbarContent === 'function' ? toolbarContent(searchToggle) : toolbarContent}
                {!toolbarReplaced && (expanded && search ? searchInput : !rangeBelowToolbar && range)}
                {!toolbarReplaced && rangeEnd ? (
                  <div className="participant-table__range-end">{rangeEnd}</div>
                ) : null}
                {typeof toolbarContent !== 'function' && searchToggle}
                {(!expanded || toolbarReplaced) && toolbarActions}
              </div>
            ) : null
          ) : (
            <>
              {showSearch && search ? (
                <div className="participant-table__toolbar">{searchInput}</div>
              ) : null}
              {(showPagination && pagination) || rangeEnd ? (
                <div className="participant-table__range-row">
                  {showPagination ? range : null}
                  {rangeEnd ? <div className="participant-table__range-end">{rangeEnd}</div> : null}
                </div>
              ) : null}
            </>
          )}

          {rangeBelowToolbar && <div className="participant-table__range-row">{rangeControls}{range}</div>}

          <div className="participant-table__list">
            <div className="participant-table__list-anchor" aria-hidden="true" />
            {isEmpty && empty ? empty : children}
          </div>

          {footer || (showPagination && pagination) ? (
            <footer className="participant-table__footer">
              {footer ??
                (pagination ? (
                  <Pagination
                    page={pagination.page}
                    pageCount={pagination.pageCount}
                    onChange={pagination.onChange}
                    previousLabel={pagination.previousLabel}
                    nextLabel={pagination.nextLabel}
                    pageLabel={pagination.pageLabel}
                    testId={pagination.testId ?? testIdFor(testId, 'pages')}
                  />
                ) : null)}
            </footer>
          ) : null}
          {actions ? <div className="participant-table__actions">{actions}</div> : null}
        </TableSurface>
      </div>
    );
  },
);
